import { camelizeKeys } from 'humps';

import { CrmType } from '~/enums/CrmType';
import { everest, evi2ken } from '~/services/serviceInstances';
import { Nullable } from '~/types/Nullable';

interface Error {
  message?: string;
}

interface CrmIntegrationConfig {}

export interface CrmIntegration {
  config: CrmIntegrationConfig;
  disabled?: boolean;
  id: Nullable<string>;
  error: Nullable<Error>;
  isDefault: boolean;
  provider: CrmType;
}

interface GetCrmsResponse {
  data: CrmIntegration[];
}

interface GetLoginRequest {
  integrationUrl: string;
}

interface GetLoginResponse {
  redirectUrl: string;
}

interface CrmSyncPair {
  id: Nullable<string>;
  orgId: string;
  link: string;
  createdBy: number;
  apiUser: number;
  dateCreated: string;
  dateSynced: string;
  status: string;
  error?: Nullable<Error>;
  provider?: CrmType;
}

// if crm does not exist from the server, create an empty crm without an ID
export const getCrmIntegrations = async (): Promise<CrmIntegration[]> => {
  function createEmptyCrmIntegration(provider: CrmType): CrmIntegration {
    return {
      id: null,
      config: {},
      error: null,
      isDefault: false,
      provider,
    };
  }

  const response = (await everest.get('/crms')) as GetCrmsResponse;

  const crmIntegrations = Object.values(CrmType).map((crmType) => {
    const crmIntegration = response.data.find(
      (record) => record.provider === crmType.toUpperCase(),
    );
    return crmIntegration
      ? { ...crmIntegration, provider: crmType }
      : createEmptyCrmIntegration(crmType);
  });

  return crmIntegrations;
};

export const deleteCrmIntegration = async (
  integration: CrmIntegration,
): Promise<string> => {
  return await everest.remove(`/crms/${integration.id}`);
};

export const getCrmIntegrationLoginUrl = async ({
  integrationUrl,
}: GetLoginRequest): Promise<GetLoginResponse> => {
  return await everest.post('/salesforce/login', { integrationUrl });
};

export const getCrmConnections = async (): Promise<CrmSyncPair[]> => {
  const response = (await everest.get('/crms')) as GetCrmsResponse;
  return camelizeKeys(response.data) as CrmSyncPair[];
};

export const getCrmIntegrationLoginUrlFromEvi2ken = async ({
  integrationUrl,
}: GetLoginRequest): Promise<GetLoginResponse> => {
  return await evi2ken.post('oauth/ac/tokens', {
    data: {
      type: 'oauth-ac-token',
      attributes: {
        provider: CrmType.Salesforce,
        returnUrl: integrationUrl,
        meta: { returnUrl: integrationUrl },
      },
    },
  });
};
